import { type Option } from '~/components/SmartSelect';
import { countries, type CountryIso2 } from '~/constants/countries';
import { currencies, type Currency } from '~/constants/currency';
import { WeightUnit } from '~/constants/weight';
import { type Country } from '~/types/Address';

type JSONCustomsItem = {
  converted_mass_unit: string;
  converted_net_weight: string;
  converted_unit_weight: string;
  description: string;
  eccn_ear99: Maybe<string>;
  hs_code: Maybe<string>;
  id: number;
  is_excluded?: boolean;
  line_item: Maybe<number>;
  origin_country: string;
  original_mass_unit: string;
  original_net_weight: string;
  original_unit_weight: string;
  quantity: number;
  sku_code: Maybe<string>;
  tariff_number: Maybe<string>;
  unit_value: number | string;
  value_amount: number | string;
  value_currency: string;
};

type CustomsItem = {
  convertedMassUnit: WeightUnit;
  convertedNetWeight: string;
  convertedUnitWeight: number;
  description: string;
  eccnEar99: Maybe<string>;
  hsCode: Maybe<string>;
  id: number;
  isExcluded?: boolean;
  lineItemId?: number;
  originCountry: Country;
  originalMassUnit: WeightUnit;
  originalNetWeight: string;
  originalUnitWeight: string;
  quantity: number;
  skuCode: Maybe<string>;
  tariffNumber: Maybe<string>;
  unitValue: number | string;
  valueAmount: number | string;
  valueCurrency: Maybe<Currency>;
};

type CustomsItemPayload = {
  description: string;
  eccnEar99: string;
  id?: number;
  isExcluded?: boolean;
  lineItemId?: number;
  massUnit: Option;
  originCountry: Country;
  originalMassUnit: Option;
  originalNetWeight: string;
  originalUnitWeight: string;
  quantity: string;
  skuCode: string;
  tariffNumber: string;
  unitValue: string;
  unitWeight: string;
  valueCurrency: Option;
};

type JSONCustomsItemPayload = {
  description: string;
  eccn_ear99: string;
  id?: number;
  is_excluded?: boolean;
  line_item: Maybe<number>;
  mass_unit: string;
  origin_country: string;
  original_mass_unit: string;
  original_net_weight: string;
  original_unit_weight: string;
  quantity: number;
  sku_code: string;
  tariff_number: string;
  unit_value: string;
  unit_weight: string;
  value_currency: string;
};

type JSONBatchEntriesCustomsItems = {
  batch_entries: [
    {
      customs_items: JSONCustomsItem[];
      id: number;
    },
  ];
};

export type WebCustomsItem = {
  /** export control classification number (ECCN/EAR99) for US exports */
  control_classification: string;
  description: string;
  is_excluded: boolean;
  is_test: boolean;
  /** @format uuid */
  line_item_object_id: string;
  mass_unit: WeightUnit;
  metadata: string;
  net_value: string;
  net_weight: string;
  /** @format uuid */
  object_id: string;
  object_state: 'INVALID' | 'VALID';
  /**
   * ISO 3166-2 alpha-2 country code
   * @example "US"
   */
  origin_country: CountryIso2;
  quantity: number;
  sku_code: string;
  /** The item's tariff number, e.g. Harmonized System (HS) Codes */
  tariff_number: string;
  unit_value: string;
  unit_weight: string;
  /**
   * ISO 4217 3-letter currency code
   * @example "USD"
   */
  value_currency: string;
};

const customsItemToPayload = (customsItem: CustomsItem): CustomsItemPayload => ({
  description: customsItem.description,
  eccnEar99: customsItem.eccnEar99 || '',
  id: customsItem.id,
  isExcluded: customsItem.isExcluded,
  lineItemId: customsItem.lineItemId,
  massUnit: { label: customsItem.convertedMassUnit, value: customsItem.convertedMassUnit },
  originCountry: customsItem.originCountry,
  originalMassUnit: { label: customsItem.originalMassUnit, value: customsItem.originalMassUnit },
  originalNetWeight: customsItem.originalNetWeight,
  originalUnitWeight: customsItem.originalUnitWeight,
  quantity: customsItem.quantity.toString(),
  skuCode: customsItem.skuCode || '',
  tariffNumber: customsItem.tariffNumber || '',
  unitValue: customsItem.unitValue.toString(),
  unitWeight: customsItem.convertedUnitWeight.toString(),
  valueCurrency: {
    label: customsItem.valueCurrency?.iso || '',
    value: customsItem.valueCurrency?.iso || '',
  },
});

const payloadToJSON = (customsItemPayload: CustomsItemPayload): JSONCustomsItemPayload => ({
  description: customsItemPayload.description,
  // eccnEar99 has a custom validator so it's treated as a required field even though it's not marked as such
  eccn_ear99: customsItemPayload.eccnEar99 || '',
  id: customsItemPayload.id,
  is_excluded: customsItemPayload.isExcluded,
  line_item: customsItemPayload.lineItemId ? customsItemPayload.lineItemId : null,
  mass_unit: customsItemPayload.originalMassUnit.value as string,
  origin_country: customsItemPayload.originCountry.iso2,
  original_mass_unit: customsItemPayload.originalMassUnit.value as string,
  original_net_weight: customsItemPayload.originalNetWeight?.toString(),
  original_unit_weight: customsItemPayload.originalUnitWeight.toString(),
  quantity: Number(customsItemPayload.quantity.toString().trim()),
  // eccnEar99 must always be included in the payload due to quirky Golang deserializer behavior -
  sku_code: customsItemPayload.skuCode,
  tariff_number: customsItemPayload.tariffNumber,
  unit_value: customsItemPayload.unitValue.toString().trim(),
  unit_weight: customsItemPayload.originalUnitWeight.toString(),
  value_currency: customsItemPayload.valueCurrency.value as string,
});

const fromJSON = (jsonCustomsItem: JSONCustomsItem): CustomsItem => {
  const {
    converted_mass_unit: mass_unit,
    converted_net_weight: net_weight,
    converted_unit_weight: unit_weight,
    description,
    eccn_ear99,
    hs_code,
    id,
    is_excluded,
    line_item,
    origin_country,
    original_mass_unit,
    original_net_weight,
    original_unit_weight,
    quantity,
    sku_code,
    tariff_number,
    unit_value,
    value_amount,
    value_currency,
  } = jsonCustomsItem;

  return {
    convertedMassUnit: <WeightUnit>mass_unit,
    convertedNetWeight: net_weight,
    convertedUnitWeight: Number(unit_weight),
    description,
    eccnEar99: eccn_ear99,
    hsCode: hs_code,
    id,
    isExcluded: is_excluded,
    lineItemId: line_item ? line_item : undefined,
    originCountry: countries[origin_country.toUpperCase()],
    originalMassUnit: <WeightUnit>original_mass_unit,
    originalNetWeight: original_net_weight,
    originalUnitWeight: original_unit_weight,
    quantity,
    skuCode: sku_code,
    tariffNumber: tariff_number,
    unitValue: Number(unit_value),
    valueAmount: value_amount,
    valueCurrency:
      Object.values(currencies).find(
        (currency) => currency.iso === value_currency?.toUpperCase(),
      ) || null,
  };
};

export const fromWebCustomsItem = (webCustomsItem: WebCustomsItem): CustomsItem => {
  const currency = currencies[webCustomsItem.value_currency.toUpperCase()] || null;
  const country = countries[webCustomsItem.origin_country.toUpperCase()];

  return {
    convertedMassUnit: WeightUnit.LB,
    convertedNetWeight: '',
    convertedUnitWeight: 0,
    description: webCustomsItem.description,
    eccnEar99: webCustomsItem.control_classification,
    hsCode: '',
    // nulls
    id: -1,
    isExcluded: webCustomsItem.is_excluded,
    lineItemId: undefined,
    originCountry: country,
    originalMassUnit: <WeightUnit>webCustomsItem.mass_unit,
    originalNetWeight: webCustomsItem.net_weight,
    originalUnitWeight: webCustomsItem.unit_weight,

    quantity: webCustomsItem.quantity,
    skuCode: webCustomsItem.sku_code,
    tariffNumber: webCustomsItem.tariff_number,
    unitValue: webCustomsItem.unit_value,
    valueAmount: webCustomsItem.net_value,
    valueCurrency: currency,
  };
};

const isEqualToPayload = (a: CustomsItem, b: CustomsItemPayload): boolean =>
  (a.lineItemId && b.lineItemId && a.lineItemId === b.lineItemId) ||
  (a.description === b.description &&
    Number(a.convertedUnitWeight) === Number(b.unitWeight) &&
    Number(a.unitValue) === Number(b.unitValue) &&
    Number(a.quantity) === Number(b.quantity) &&
    a.valueCurrency?.iso === b.valueCurrency.value &&
    a.tariffNumber === b.tariffNumber &&
    a.originCountry.iso2 === b.originCountry.iso2 &&
    (a.eccnEar99 === b.eccnEar99 || (!a.eccnEar99 && !b.eccnEar99)));

export {
  CustomsItem,
  CustomsItemPayload,
  JSONBatchEntriesCustomsItems,
  JSONCustomsItem,
  JSONCustomsItemPayload,
  customsItemToPayload,
  fromJSON,
  isEqualToPayload,
  payloadToJSON,
};
export default CustomsItem;
